import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { auth } from '../utils/auth';

const navItems = [
  { path: '/', label: 'Overview', match: '/' },
  { path: '/users', label: 'User Management', match: '/users' },
  { path: '/brokers', label: 'Broker Authentication', match: '/brokers' },
  { path: '/strategies', label: 'Strategy Management', match: '/strategies' },
  { path: '/risk', label: 'Risk Controls', match: '/risk' }
];

const pageMeta = {
  '/': {
    title: 'Dashboard',
    description: 'Dummy data mirrors the requested admin modules and is ready for future API integration.'
  },
  '/users': {
    title: 'User Management',
    description: 'Review registrations, subscriptions, account state, and user-level control actions.'
  },
  '/brokers': {
    title: 'Broker Authentication',
    description: 'Monitor Fyers connection health, token status, and internal broker configuration.'
  },
  '/strategies': {
    title: 'Strategy Management',
    description: 'Control strategy state, rollout mode, and platform-wide trading behavior.'
  },
  '/risk': {
    title: 'Risk Controls',
    description: 'Track high-visibility risk signals and centralize emergency intervention actions.'
  }
};

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

function unwrapList(response) {
  const parsedBody =
    typeof response?.body === 'string'
      ? safeJsonParse(response.body)
      : typeof response?.data?.body === 'string'
        ? safeJsonParse(response.data.body)
        : null;

  const candidates = [
    response,
    response?.data,
    response?.users,
    response?.data?.users,
    parsedBody,
    parsedBody?.users,
    parsedBody?.data
  ];

  return candidates.find(Array.isArray) || [];
}

function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { api } = useAppContext();
  const currentMeta = pageMeta[location.pathname] || pageMeta['/'];
  const [headerStats, setHeaderStats] = useState({
    pendingApprovals: '--',
    expiredBrokerTokens: '--'
  });

  useEffect(() => {
    let isMounted = true;

    const loadHeaderStats = async () => {
      try {
        const [usersResponse, tokenResponse] = await Promise.all([
          api.getAdminUsers(),
          api.getTokenValidation()
        ]);

        if (!isMounted) {
          return;
        }

        const pendingUsers = unwrapList(usersResponse).filter(
          (user) => String(user.status || '').toUpperCase() === 'UNCONFIRMED'
        );
        const isTokenHealthy = tokenResponse?.data?.s === 'ok' && tokenResponse?.data?.code === 200;

        setHeaderStats({
          pendingApprovals: pendingUsers.length,
          expiredBrokerTokens: isTokenHealthy ? 0 : 1
        });
      } catch (error) {
        if (isMounted) {
          setHeaderStats({
            pendingApprovals: '--',
            expiredBrokerTokens: '--'
          });
        }
      }
    };

    loadHeaderStats();

    return () => {
      isMounted = false;
    };
  }, [api]);

  const handleLogout = () => {
    auth.logout();
    navigate('/login', { replace: true });
  };

  const handleForceTokenRefresh = () => {
    navigate('/fyers/login', { replace: true });
  };

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div>
          <p className="eyebrow">Scheduler</p>
          <h2>Admin Control Panel</h2>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <button type="button" className="ghost-button" onClick={handleForceTokenRefresh}>
          Refresh Fyers Token
        </button>
        <button type="button" className="ghost-button" onClick={handleLogout}>
          Log Out
        </button>
      </aside>

      <main className="main-content">
        <header className="page-header">
          <div>
            <p className="eyebrow">Live Prototype</p>
            <h1>{currentMeta.title}</h1>
            <p className="supporting-text dark-text">{currentMeta.description}</p>
          </div>
          {[
            { label: 'Pending approvals', value: headerStats.pendingApprovals },
            { label: 'Expired broker tokens', value: headerStats.expiredBrokerTokens }
          ].map((stat) => (
            <div key={stat.label} className="header-stat">
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
            </div>
          ))}
        </header>

        <Outlet />
      </main>
    </div>
  );
}

export default DashboardLayout;
